import { useQueries } from '@tanstack/react-query';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  Bird,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  HeartPulse,
  Package,
  Weight,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AppHeaderContent } from '@/components/AppHeaderContent';
import { HomeSummary } from '@/components/HomeSummary';
import { NaveCard } from '@/components/NaveCard';
import { apiFetch, extractApiMessage } from '@/lib/api';
import { fmt1, fmtInt } from '@/lib/format';
import { sampleSummary } from '@/lib/homeSummary';
import { formatoDecimal, formatoEuros } from '@/lib/liquidacion';
import { cacheReadThrough } from '@/lib/offline/cache';
import { useCamadaActiva } from '@/lib/queries/camadas';
import { qk } from '@/lib/queries/client';
import { useEntregasDeCamada } from '@/lib/queries/entregasPienso';
import { useLiquidacionEvolucion } from '@/lib/queries/liquidacion';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { useNaves } from '@/lib/queries/naves';
import { useCurvaEstandar } from '@/lib/queries/pesajes';
import { useParteDelDiaPorCamada } from '@/lib/queries/registrosDiarios';
import { useSalidasCamada } from '@/lib/queries/salidas';
import type { Pesaje, RegistroDiario } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const ownerId = user?.id ?? null;
  const today = format(new Date(), 'yyyy-MM-dd');
  const camadaQ = useCamadaActiva();
  const camada = camadaQ.data ?? null;
  const camadaId = camada?.id ?? null;
  const lotesQ = useLotesDeCamada(camadaId);
  const lotes = useMemo(() => lotesQ.data ?? [], [lotesQ.data]);
  const navesQ = useNaves();
  const parteQ = useParteDelDiaPorCamada(camadaId, today);
  const salidasQ = useSalidasCamada(camadaId);
  const entregasQ = useEntregasDeCamada(camadaId);
  const curvaQ = useCurvaEstandar(lotes[0]?.id ?? null);
  const canSeeFinance = user != null && user.rol !== 'operario';
  const canManage = user?.rol === 'admin' || user?.rol === 'ganadero';
  const evolucionQ = useLiquidacionEvolucion(canSeeFinance ? camadaId : null);

  // Mismas claves y almacenamiento que el detalle de nave; sin una segunda caché.
  const registrosQ = useQueries({
    queries: lotes.map((lote) => {
      const key = [...qk('registros-diarios'), 'lote', lote.id, ''];
      return {
        queryKey: [...key, 'owner', ownerId],
        queryFn: () =>
          cacheReadThrough(key, () =>
            apiFetch<RegistroDiario[]>(`/v1/lotes-nave/${lote.id}/registros-diarios`),
          ),
        networkMode: 'always' as const,
        staleTime: 30_000,
      };
    }),
  });
  const pesajesQ = useQueries({
    queries: lotes.map((lote) => {
      const key = [...qk('pesajes'), 'lote', lote.id];
      return {
        queryKey: [...key, 'owner', ownerId],
        queryFn: () =>
          cacheReadThrough(key, () => apiFetch<Pesaje[]>(`/v1/lotes-nave/${lote.id}/pesajes`)),
        networkMode: 'always' as const,
        staleTime: 30_000,
      };
    }),
  });
  const registros =
    lotesQ.data == null || registrosQ.some((q) => q.data == null)
      ? undefined
      : registrosQ.flatMap((q) => q.data!).filter((r) => r.fecha <= today);
  const pesajes =
    lotesQ.data == null || pesajesQ.some((q) => q.data == null)
      ? undefined
      : pesajesQ.flatMap((q) => q.data!);
  const salidas = salidasQ.data?.filter((s) => !s.anulada && s.fecha_carga <= today);
  const entregas = entregasQ.data?.filter((e) => e.fecha <= today);
  const bajas = registros?.reduce((n, r) => n + r.bajas_muerte + r.bajas_triaje, 0);
  const vivas =
    bajas == null || salidas == null
      ? null
      : Math.max(
          (camada?.num_animales_total ?? 0) -
            bajas -
            salidas.reduce((n, s) => n + s.unidades_cargadas_total, 0),
          0,
        );
  const mortalidad =
    bajas != null && camada?.num_animales_total ? (bajas / camada.num_animales_total) * 100 : null;
  const pienso = entregas?.reduce((n, e) => n + e.kg, 0);
  const sinAsignar =
    camada && lotesQ.data
      ? Math.max(
          camada.num_animales_total - lotes.reduce((n, l) => n + l.num_animales_asignados, 0),
          0,
        )
      : null;
  const muestra =
    camada && pesajes
      ? sampleSummary(pesajes, today, camada.fecha_entrada, curvaQ.data?.puntos ?? [])
      : null;
  const completed = lotes.filter((l) =>
    parteQ.data?.registros.some((r) => r.lote_nave_id === l.id && r.id != null),
  ).length;
  const partesKnown = parteQ.data != null && lotesQ.data != null;
  const allComplete = partesKnown && lotes.length > 0 && completed === lotes.length;
  const estimation = evolucionQ.data?.serie.find((d) => d.fecha === today);
  const dia = camada
    ? Math.max(differenceInCalendarDays(parseISO(today), parseISO(camada.fecha_entrada)) + 1, 1)
    : null;
  const onParte = () =>
    navigate(`/camadas/${camadaId}/registro-diario?fecha=${today}`, {
      state: { backgroundLocation: location },
    });
  const onFeed = () =>
    navigate(`/camadas/${camadaId}/entregas-pienso/nueva`, {
      state: { backgroundLocation: location },
    });

  return (
    <main className="home-page mx-auto max-w-md px-4">
      <h1 className="sr-only">{t('navigation.home')}</h1>
      <header className="ui-global-header mb-3" data-testid="app-header">
        <AppHeaderContent />
      </header>
      {camadaQ.isLoading && (
        <p role="status" className="py-8 text-center">
          {t('common.loading')}
        </p>
      )}
      {camadaQ.error && !camada && <p role="alert">{extractApiMessage(camadaQ.error)}</p>}
      {!camadaQ.isLoading && !camada && !camadaQ.error && (
        <section data-testid="home-sin-camada" className="space-y-4 py-4">
          <p>{t('home.sin_camada_descripcion')}</p>
          {canManage && (
            <button className="ui-primary w-full" onClick={() => navigate('/camadas/nueva')}>
              {t('home.sin_camada_cta')}
            </button>
          )}
        </section>
      )}
      {camada && (
        <div className="home-sections">
          <section className="home-hero" data-testid="home-con-camada" aria-label="Camada actual">
            <div className="home-hero-header">
              <h2>
                <Link to={`/camadas/${camada.id}`} className="home-hero-title">
                  <span className="home-hero-name" title={camada.codigo_lote}>
                    Camada {camada.codigo_lote}
                  </span>
                  <ChevronRight aria-hidden size={18} />
                </Link>
              </h2>
              <p
                className="home-day"
                aria-label={`Día ${dia} de ${camada.fase === 'recria' ? 'recría' : 'engorde'}`}
              >
                Día {dia}
              </p>
            </div>
            <dl className="home-kpis">
              {[
                {
                  Icon: Bird,
                  title: 'Aves vivas',
                  value: vivas == null ? '—' : fmtInt(vivas),
                  test: 'home-kpi-aves-vivas',
                },
                {
                  Icon: HeartPulse,
                  title: 'Mortalidad',
                  value: mortalidad == null ? '—' : fmt1(mortalidad),
                  unit: '%',
                },
                {
                  Icon: Weight,
                  title: 'Último peso',
                  value: muestra ? formatoDecimal(muestra.peso, 2) : '—',
                  unit: 'kg',
                },
                {
                  Icon: Package,
                  title: 'Pienso',
                  value: pienso == null ? '—' : fmt1(pienso / 1000),
                  unit: 't',
                  test: 'pienso-kpi-total',
                },
              ].map(({ Icon, title, value, test, unit }) => (
                <div key={title}>
                  <dt>
                    <span className="home-kpi-icon">
                      <Icon size={18} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span>{title}</span>
                  </dt>
                  <dd data-testid={test}>
                    {value}
                    {unit && value !== '—' && (
                      <>
                        {' '}
                        <small>{unit}</small>
                      </>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {!partesKnown && <p className="home-hero-note">Datos pendientes de actualizar</p>}
            {muestra && muestra.lotes < lotes.length && (
              <p className="home-hero-note">
                Peso: muestra de {muestra.lotes}/{lotes.length} naves
              </p>
            )}
            {!!sinAsignar && (
              <p className="home-hero-note">{fmtInt(sinAsignar)} aves pendientes de asignar</p>
            )}
          </section>

          <section aria-labelledby="home-naves-title">
            <div className="home-section-heading">
              <h2 id="home-naves-title">Naves</h2>
              <span className={allComplete ? 'text-ok-text' : 'text-text-secondary'}>
                {allComplete && <CheckCircle2 size={14} aria-hidden />}
                {parteQ.data?.local_pending
                  ? 'Partes en el móvil'
                  : partesKnown
                    ? `${completed}/${lotes.length} partes`
                    : 'Partes sin actualizar'}
              </span>
            </div>
            {navesQ.isLoading && <p role="status">{t('common.loading')}</p>}
            {navesQ.error && !navesQ.data && <p>Naves no disponibles. Conecta para actualizar.</p>}
            {lotesQ.error && !lotesQ.data && (
              <p>Asignaciones no disponibles. Conecta para actualizar.</p>
            )}
            {navesQ.data?.length === 0 && <p>{t('home.sin_naves')}</p>}
            {lotesQ.data &&
              navesQ.data?.map((nave) => {
                const index = lotes.findIndex((l) => l.nave_id === nave.id);
                const lote = lotes[index] ?? null;
                return (
                  <NaveCard
                    key={nave.id}
                    nave={nave}
                    lote={lote}
                    registros={registrosQ[index]?.data?.filter((r) => r.fecha <= today)}
                    salidaUnidades={salidas
                      ?.flatMap((s) => s.lineas)
                      .filter((l) => l.lote_nave_id === lote?.id)
                      .reduce((n, l) => n + l.unidades_cargadas, 0)}
                    salidasCargando={salidas == null}
                    camadaId={camada.id}
                    parte={
                      parteQ.data
                        ? (parteQ.data.registros.find((r) => r.lote_nave_id === lote?.id) ?? null)
                        : undefined
                    }
                    localPending={parteQ.data?.local_pending}
                    canAssign={canManage}
                  />
                );
              })}
          </section>
          {canManage && (
            <div data-testid="home-acciones-dia">
              {lotesQ.data?.length === 0 ? (
                <div className="space-y-2">
                  <p>{t('home.camada_sin_lotes_descripcion')}</p>
                  <button
                    className="ui-primary w-full"
                    onClick={() => navigate(`/camadas/${camada.id}/asignar-lotes`)}
                  >
                    {t('home.camada_sin_lotes_cta')}
                  </button>
                </div>
              ) : (
                <button
                  className={`ui-primary home-part-action ${allComplete ? 'home-part-complete' : ''}`}
                  disabled={!lotes.length}
                  onClick={onParte}
                >
                  <ClipboardCheck size={20} aria-hidden />
                  <span>{allComplete ? 'Revisar partes de hoy' : 'Registrar parte de hoy'}</span>
                  <ChevronRight size={18} aria-hidden />
                </button>
              )}
            </div>
          )}
          <HomeSummary
            entrada={camada.fecha_entrada}
            today={today}
            lotes={lotesQ.data?.map((l) => l.id)}
            registros={registros}
            entregas={entregas}
            pesajes={pesajes}
            curva={curvaQ.data ?? null}
            completos={allComplete}
            onFeed={canManage ? onFeed : undefined}
          />
          {canSeeFinance && (
            <Link className="home-finance" to={`/liquidacion/${camada.id}/evolucion`}>
              <span>
                <span className="home-finance-title">
                  Liquidación estimada <span className="home-provisional">Provisional</span>
                </span>
                <strong>
                  {estimation?.resultado ? formatoEuros(estimation.resultado.total_eur) : '—'}
                </strong>
              </span>
              <span className="home-finance-access">
                Evolución <ChevronRight size={16} aria-hidden />
              </span>
              <span className="home-finance-note">
                {estimation?.resultado
                  ? 'Si salieran hoy · Pienso entregado como referencia'
                  : evolucionQ.isLoading
                    ? 'Calculando…'
                    : 'Estimación no disponible para hoy'}
              </span>
              {estimation &&
                (estimation.partes_pendientes > 0 ||
                  !estimation.comparable ||
                  estimation.advertencias.length > 0) && (
                  <span className="home-finance-note">
                    Datos incompletos o estimados · Consultar detalle
                  </span>
                )}
            </Link>
          )}
        </div>
      )}
    </main>
  );
}

export default HomePage;
